import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  MapPin,
  Calendar,
  Users,
  Star,
  CheckCircle,
  XCircle,
  FileText,
  ChevronLeft,
  ChevronRight,
  Building2,
  X,
  Search,
  ChevronDown,
  ChevronUp,
  Check,
  Utensils,
  Truck,
} from "lucide-react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import MailIcon from "@mui/icons-material/Mail";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { KeyUri } from "../key";
import TourBookingSheet from "./TourBooking";
import { trackEvent } from "../utils/metaPixel";
import SafeImage, { NoImage, normalizeImages } from "../Global/SafeTmage";

// ── Per-stop hotel grouping helpers (mirrors admin CreateTourPage/EditTourPage) ──
const placeKey = (label) => (label || "").trim().toLowerCase();

const groupHotelsByPlace = (hotels) => {
  const groups = [];
  const keyToGroup = {};
  (hotels || []).forEach((h, idx) => {
    const label = (h.location_label || "").trim();
    const key = label ? placeKey(label) : `__blank_${idx}`;
    if (!keyToGroup[key]) {
      const group = { key, label: label || null, hotels: [] };
      keyToGroup[key] = group;
      groups.push(group);
    }
    keyToGroup[key].hotels.push(h);
  });
  return groups;
};

const isSameRoom = (a, b) =>
  !!a && !!b && a.room_type === b.room_type && a.room_name === b.room_name;

const buildStopGroups = (hotels) => {
  const placeGroups = groupHotelsByPlace(hotels);
  return placeGroups.map((pg) => {
    const hotelGroups = [];
    pg.hotels.forEach((hotel) => {
      const hKey = hotel.propertyId || hotel.hotel_name;
      const existing = hotelGroups.find((g) => g.key === hKey);
      if (existing) {
        (hotel.selected_rooms || []).forEach((sr) => {
          if (!existing.rooms.find((r) => isSameRoom(r, sr)))
            existing.rooms.push(sr);
        });
      } else {
        hotelGroups.push({
          key: hKey,
          hotel,
          rooms:
            hotel.property_type === "existing"
              ? [...(hotel.selected_rooms || [])]
              : [
                  {
                    room_type: hotel.room_type || "Standard",
                    room_name: hotel.room_type || "Standard",
                    meal_plan: hotel.meal_plan || "ro",
                    price_per_night: hotel.price_per_night || 0,
                    notes: hotel.notes || "",
                  },
                ],
        });
      }
    });
    return { ...pg, hotelGroups };
  });
};

const mealLabels = {
  ro: "Room Only",
  cp: "Breakfast included",
  map: "Breakfast + Dinner",
  ap: "All Meals",
};

const amenityLabels = (room) => {
  const tags = [];
  if (
    room?.meal_plan &&
    mealLabels[room.meal_plan] &&
    room.meal_plan !== "ro"
  ) {
    tags.push(mealLabels[room.meal_plan]);
  }
  return tags;
};

const lightboxImg = {
  width: "auto",
  height: "auto",
  maxHeight: "75vh",
  maxWidth: "100%",
  minWidth: 200,
  minHeight: 120,
  objectFit: "contain",
  borderRadius: 3,
};

/* Shared section-card look (same as WellnessDetail) */
const CARD =
  "bg-white rounded-3xl border border-gray-100 shadow-sm p-6 lg:p-10";
const H2 = "text-2xl lg:text-3xl font-serif font-bold text-slate-900";

/* ───────────────────────── Hero ───────────────────────── */

/* Badges + title + meta row. `onImage` = white text version used on the desktop hero overlay. */
const HeroMeta = ({ tour, avgRating, reviewCount, onImage = false }) => (
  <div className={onImage ? "text-white max-w-2xl" : "max-w-4xl"}>
    <div className="flex flex-wrap gap-2 mb-4">
      {tour.is_group_tour && (
        <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase shadow-sm">
          Group tour
        </span>
      )}
      {tour.duration && (
        <span
          className={`px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase ${
            onImage
              ? "bg-white/20 backdrop-blur-md text-white"
              : "bg-orange-50 text-orange-800 border border-orange-100"
          }`}
        >
          {tour.duration}
        </span>
      )}
    </div>

    <h1
      className={`font-serif font-bold leading-tight ${
        onImage
          ? "text-4xl lg:text-5xl text-white mb-3"
          : "text-3xl sm:text-4xl text-slate-900 mb-4"
      }`}
    >
      {tour.title}
    </h1>

    <div
      className={`flex flex-wrap gap-x-6 gap-y-2 text-sm ${onImage ? "text-slate-200" : "text-slate-600"}`}
    >
      {tour.location && (
        <div className="flex items-center gap-2">
          <MapPin
            size={18}
            className={onImage ? "text-orange-300" : "text-orange-600"}
          />
          <span>{tour.location}</span>
        </div>
      )}
      {tour.group_size && (
        <div className="flex items-center gap-2">
          <Users
            size={18}
            className={onImage ? "text-orange-300" : "text-orange-600"}
          />
          <span>{tour.group_size} people</span>
        </div>
      )}
      {avgRating > 0 && (
        <div className="flex items-center gap-2">
          <Star size={18} className="fill-yellow-400 text-yellow-400" />
          <span>
            {avgRating.toFixed(1)} ({reviewCount} reviews)
          </span>
        </div>
      )}
    </div>
  </div>
);

const TourDetailImageGallery = ({ images, title, overlay, fallback }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [openGallery, setOpenGallery] = useState(false);
  const allImages = useMemo(() => normalizeImages(images), [images]);

  if (allImages.length === 0) {
    return (
      <>
        <NoImage
          type="tour"
          sx={{ height: { xs: 260, md: 500 }, borderRadius: 4 }}
        />
        {fallback && <div className="hidden md:block mt-6">{fallback}</div>}
      </>
    );
  }

  const prev = () =>
    setSelectedImage((i) => (i - 1 + allImages.length) % allImages.length);
  const next = () => setSelectedImage((i) => (i + 1) % allImages.length);

  return (
    <>
      {/* Desktop hero with title overlay */}
      <div className="hidden md:block relative h-[500px] rounded-3xl overflow-hidden shadow-lg">
        <div
          className="absolute inset-0 cursor-pointer"
          onClick={() => setOpenGallery(true)}
        >
          <SafeImage src={allImages[selectedImage]} type="tour" alt={title} />
        </div>

        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent pointer-events-none" />

        {allImages.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur rounded-full p-2 shadow-md hover:scale-110 transition"
            >
              <ChevronLeft className="w-5 h-5 text-gray-800" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur rounded-full p-2 shadow-md hover:scale-110 transition"
            >
              <ChevronRight className="w-5 h-5 text-gray-800" />
            </button>
            <button
              onClick={() => setOpenGallery(true)}
              className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur transition"
            >
              View all {allImages.length} photos
            </button>
          </>
        )}

        <div className="absolute inset-x-0 bottom-0 p-8 lg:p-10 text-white pointer-events-none">
          {overlay}
        </div>
      </div>

      {/* Mobile main image */}
      <div className="md:hidden relative w-full h-[300px] rounded-2xl overflow-hidden">
        <div className="absolute inset-0" onClick={() => setOpenGallery(true)}>
          <SafeImage src={allImages[selectedImage]} type="tour" alt={title} />
        </div>
        {allImages.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur rounded-full p-2 shadow-md active:scale-95"
            >
              <ChevronLeft className="w-5 h-5 text-gray-800" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur rounded-full p-2 shadow-md active:scale-95"
            >
              <ChevronRight className="w-5 h-5 text-gray-800" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full">
              {selectedImage + 1} / {allImages.length}
            </div>
          </>
        )}
      </div>

      {/* Thumbnails (mobile + desktop) */}
      {allImages.length > 1 && (
        <div className="flex gap-2 md:gap-3 mt-2 md:mt-4 overflow-x-auto scrollbar-hide p-1">
          {allImages.map((img, idx) => (
            <div
              key={idx}
              onClick={() => setSelectedImage(idx)}
              className={`flex-shrink-0 w-[80px] h-[60px] md:w-[120px] md:h-[84px] rounded-lg md:rounded-xl overflow-hidden cursor-pointer transition-all duration-300
                ${
                  selectedImage === idx
                    ? "ring-2 ring-orange-500 scale-105"
                    : "ring-2 ring-transparent opacity-60 hover:opacity-100"
                }`}
            >
              <SafeImage
                src={img}
                type="tour"
                alt={`${title} ${idx + 1}`}
                compact
              />
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {openGallery && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setOpenGallery(false)}
        >
          <button
            onClick={() => setOpenGallery(false)}
            className="absolute top-4 right-4 text-white bg-white/20 hover:bg-white/30 rounded-full p-2 z-10"
          >
            <XCircle className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-white/20 hover:bg-white/30 rounded-full p-3 z-10"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>

          <div onClick={(e) => e.stopPropagation()}>
            <SafeImage
              src={allImages[selectedImage]}
              type="tour"
              alt={title}
              sx={{ ...lightboxImg, maxHeight: "90vh", maxWidth: "90vw" }}
            />
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-white/20 hover:bg-white/30 rounded-full p-3 z-10"
          >
            <ChevronRight className="w-7 h-7" />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-sm font-medium bg-black/50 px-4 py-1.5 rounded-full">
            {selectedImage + 1} / {allImages.length}
          </div>
        </div>
      )}
    </>
  );
};

/* ───────────────────────── Image strip (hotel / transport thumbnails) ───────────────────────── */

const ImageStrip = ({ images, alt, variant = "strip", type = "hotel" }) => {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [slide, setSlide] = useState(0);
  const imgs = normalizeImages(images);

  if (imgs.length === 0) {
    return (
      <div
        className={
          variant === "square"
            ? "w-24 h-24 sm:w-32 sm:h-32 flex-shrink-0 rounded-xl overflow-hidden"
            : variant === "slider"
              ? "w-full h-full"
              : "h-28 w-full rounded-xl overflow-hidden"
        }
      >
        <NoImage type={type} />
      </div>
    );
  }

  const cur = slide < imgs.length ? slide : 0; // safe if the image list shrinks
  const SHOW = 4;
  const visible = imgs.slice(0, SHOW);
  const remaining = imgs.length - SHOW;
  const openAt = (i) => {
    setGalleryIndex(i);
    setGalleryOpen(true);
  };
  const go = (e, dir) => {
    e.stopPropagation();
    setSlide(
      (s) => ((s < imgs.length ? s : 0) + dir + imgs.length) % imgs.length,
    );
  };

  const slider = (
    <>
      <SafeImage
        src={imgs[cur]}
        type={type}
        alt={`${alt}-${cur}`}
        className="hover:opacity-90 transition"
      />
      {imgs.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => go(e, -1)}
            aria-label="Previous image"
            className="absolute left-1.5 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-1 shadow hover:scale-110 transition"
          >
            <ChevronLeft className="w-4 h-4 text-gray-800" />
          </button>
          <button
            type="button"
            onClick={(e) => go(e, 1)}
            aria-label="Next image"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-1 shadow hover:scale-110 transition"
          >
            <ChevronRight className="w-4 h-4 text-gray-800" />
          </button>
          <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 bg-black/60 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-md">
            {cur + 1} / {imgs.length}
          </span>
        </>
      )}
    </>
  );

  return (
    <>
      {variant === "square" ? (
        <div
          className="relative w-24 h-24 sm:w-32 sm:h-32 flex-shrink-0 rounded-xl overflow-hidden cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            openAt(cur);
          }}
        >
          {slider}
        </div>
      ) : variant === "slider" ? (
        <div
          className="relative w-full h-full overflow-hidden cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            openAt(cur);
          }}
        >
          {slider}
        </div>
      ) : (
        <div className="flex gap-1 h-28 overflow-hidden">
          {visible.map((src, i) => {
            const isLast = i === SHOW - 1 && remaining > 0;
            return (
              <div
                key={i}
                className={`relative overflow-hidden flex-shrink-0 cursor-pointer ${i === 0 ? "flex-1" : "w-[23%]"}`}
                onClick={(e) => {
                  e.stopPropagation();
                  openAt(i);
                }}
              >
                <SafeImage
                  src={src}
                  type={type}
                  alt={`${alt}-${i}`}
                  className="hover:opacity-90 transition"
                />
                {isLast && (
                  <div className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center gap-1">
                    <span className="text-white text-xl font-bold">
                      +{remaining}
                    </span>
                    <span className="text-white text-xs">more</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {galleryOpen && (
        <div
          className="fixed inset-0 z-[99999] bg-black/95 flex items-center justify-center"
          onClick={() => setGalleryOpen(false)}
        >
          <button
            onClick={() => setGalleryOpen(false)}
            className="absolute top-4 right-4 text-white bg-white/20 hover:bg-white/30 rounded-full p-2 z-10"
          >
            <XCircle className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setGalleryIndex((i) => (i - 1 + imgs.length) % imgs.length);
            }}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-white bg-white/20 hover:bg-white/30 rounded-full p-3 z-10"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>

          <div
            onClick={(e) => e.stopPropagation()}
            className="flex flex-col items-center gap-4 max-w-5xl w-full px-16"
          >
            <SafeImage
              src={imgs[galleryIndex]}
              type={type}
              alt={alt}
              sx={lightboxImg}
            />
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide max-w-full">
              {imgs.map((src, i) => (
                <div
                  key={i}
                  onClick={() => setGalleryIndex(i)}
                  className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden cursor-pointer transition-all
                    ${galleryIndex === i ? "ring-2 ring-orange-400 scale-105" : "opacity-60 hover:opacity-100"}`}
                >
                  <SafeImage
                    src={src}
                    type={type}
                    alt={`${alt}-${i}`}
                    compact
                  />
                </div>
              ))}
            </div>
            <div className="text-white text-sm font-medium bg-black/50 px-4 py-1.5 rounded-full">
              {galleryIndex + 1} / {imgs.length}
            </div>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setGalleryIndex((i) => (i + 1) % imgs.length);
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-white bg-white/20 hover:bg-white/30 rounded-full p-3 z-10"
          >
            <ChevronRight className="w-7 h-7" />
          </button>
        </div>
      )}
    </>
  );
};

/* ───────────────────────── Hotel selection ───────────────────────── */

const HotelOptionCard = ({
  hotel,
  room,
  isSelected,
  isBaseline,
  priceLabel,
  onSelect,
}) => (
  <div
    className={`rounded-2xl border-2 overflow-hidden bg-white transition-colors
      ${isSelected ? "border-orange-500" : "border-gray-200 hover:border-gray-300"}`}
  >
    <div className="h-32 w-full overflow-hidden">
      <ImageStrip
        variant="slider"
        type="hotel"
        images={hotel.images}
        alt={hotel.hotel_name}
      />
    </div>

    <div className="p-3">
      <h4 className="font-bold text-gray-900 text-sm leading-snug">
        {hotel.hotel_name}
        {hotel.star_rating ? ` · ${hotel.star_rating}★` : ""}
      </h4>

      <p
        className={`text-xs font-semibold mt-1 ${isBaseline ? "text-gray-500" : "text-orange-600"}`}
      >
        {priceLabel}
      </p>

      {hotel.address && (
        <p className="text-[11px] text-gray-500 mt-1.5 flex items-start gap-1">
          <MapPin size={12} className="mt-0.5 flex-shrink-0" />
          {hotel.address}
        </p>
      )}

      <div className="flex flex-wrap gap-1.5 mt-2">
        {amenityLabels(room).map((tag) => (
          <span
            key={tag}
            className="text-[10px] font-medium bg-gray-100 text-gray-600 px-2 py-1 rounded-md"
          >
            {tag}
          </span>
        ))}
      </div>

      <button
        onClick={() => onSelect(hotel, room)}
        disabled={isSelected}
        className={`mt-3 w-full text-xs font-bold py-2 rounded-lg transition-colors
          ${isSelected ? "bg-orange-100 text-orange-400 cursor-default" : "bg-slate-800 text-white hover:bg-slate-900"}`}
      >
        {isSelected ? "Selected" : "Select"}
      </button>
    </div>
  </div>
);

const HotelSummaryCard = ({
  stop,
  selection,
  totalRooms,
  totalGuests,
  allowNoHotel,
  onOpenPanel,
  onClearHotel,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  if (!selection) {
    return (
      <div className="border border-dashed border-gray-300 rounded-2xl p-4 flex items-center justify-between bg-white">
        <div>
          <p className="text-sm font-semibold text-gray-700">
            No hotel{stop.label ? ` in ${stop.label}` : ""}
          </p>
          <p className="text-xs text-gray-400">
            Arrange your own accommodation
          </p>
        </div>
        <button
          onClick={onOpenPanel}
          className="text-sm font-semibold text-orange-600 hover:text-orange-700"
        >
          Add hotel
        </button>
      </div>
    );
  }

  const { hotel, room } = selection;
  const nights = Number(hotel.nights) || 1;

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white">
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-100"
      >
        <div className="flex items-center gap-2.5 text-gray-900">
          <Building2 size={17} strokeWidth={2} />
          <span className="text-sm font-semibold tracking-tight">
            Hotel · {nights} Night{nights > 1 ? "s" : ""}
            {stop.label ? ` · In ${stop.label}` : ""}
          </span>
          {collapsed ? (
            <ChevronDown size={16} className="text-gray-400" />
          ) : (
            <ChevronUp size={16} className="text-gray-400" />
          )}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {allowNoHotel && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onClearHotel();
              }}
              className="text-sm font-semibold text-gray-500 hover:text-gray-700"
            >
              Remove
            </span>
          )}
          <span className="w-px h-4 bg-gray-200" />
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onOpenPanel();
            }}
            className="text-sm font-semibold text-orange-600 hover:text-orange-700"
          >
            Change
          </span>
        </div>
      </button>

      {!collapsed && (
        <div className="p-4 flex items-start gap-4">
          <ImageStrip
            variant="square"
            type="hotel"
            images={hotel.images}
            alt={hotel.hotel_name}
          />

          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <h3 className="font-bold text-gray-900 text-[15px] leading-snug">
                {hotel.hotel_name}
              </h3>
              {hotel.star_rating && (
                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">
                  {hotel.star_rating}
                </span>
              )}
            </div>

            {hotel.address && (
              <p className="text-xs text-gray-500 mt-1">📍 {hotel.address}</p>
            )}

            <p className="flex items-center gap-1 mt-2 text-xs text-gray-500">
              <Users size={12} /> {totalRooms} Room{totalRooms > 1 ? "s" : ""} ·{" "}
              {totalGuests} Guest{totalGuests > 1 ? "s" : ""}
            </p>

            <div className="mt-3">
              <p className="text-sm font-semibold text-gray-800">
                {room.room_name || room.room_type}
              </p>
              {room.meal_plan && (
                <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                  <Utensils size={11} />{" "}
                  {mealLabels[room.meal_plan] || room.meal_plan}
                </p>
              )}
            </div>

            <button
              onClick={onOpenPanel}
              className="mt-2 text-xs font-semibold text-orange-600 hover:text-orange-700"
            >
              More room options
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ChangeHotelPanel = ({
  stop,
  currentSelection,
  allowNoHotel,
  onSelect,
  onClearHotel,
  onClose,
}) => {
  const [query, setQuery] = useState("");
  const [starFilter, setStarFilter] = useState(null);
  const [sortBy, setSortBy] = useState("popularity");

  const currentHotelName = currentSelection?.hotel?.hotel_name || null;
  const currentRoom = currentSelection?.room || null;

  const cheapestRoomOf = (rooms) => {
    if (!rooms || rooms.length === 0) return null;
    return [...rooms].sort(
      (a, b) => Number(a.price_per_night || 0) - Number(b.price_per_night || 0),
    )[0];
  };

  const baseline = (() => {
    let best = null;
    stop.hotelGroups.forEach(({ rooms }) => {
      const room = cheapestRoomOf(rooms);
      if (!room) return;
      const price = Number(room.price_per_night || 0);
      if (!best || price < best.price) best = { price };
    });
    return best?.price || 0;
  })();

  const filtered = React.useMemo(() => {
    let list = stop.hotelGroups.filter(({ hotel }) => {
      if (
        query &&
        !hotel.hotel_name.toLowerCase().includes(query.toLowerCase())
      )
        return false;
      if (starFilter && Number(hotel.star_rating) < starFilter) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      if (sortBy === "price_low") {
        return (
          Number(cheapestRoomOf(a.rooms)?.price_per_night || 0) -
          Number(cheapestRoomOf(b.rooms)?.price_per_night || 0)
        );
      }
      return 0;
    });
    return list;
  }, [stop, query, starFilter, sortBy]);

  return (
    <div className="fixed inset-0 z-[999] flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative w-full sm:max-w-xl h-full bg-white flex flex-col shadow-2xl">
        <div className="px-5 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">
              Change hotel{stop.label ? ` in ${stop.label}` : ""}
            </h2>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-gray-100"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>

          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by hotel name or location"
              className="w-full pl-9 pr-3 py-2.5 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400"
            />
          </div>

          <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
            {[3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => setStarFilter(starFilter === n ? null : n)}
                className={`flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors
                  ${starFilter === n ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
              >
                {n}★
              </button>
            ))}
          </div>
        </div>

        <div className="px-5 py-3 flex items-center justify-between text-xs text-gray-500 border-b border-gray-100">
          <span>
            Showing {filtered.length} stays
            {stop.label ? ` in ${stop.label}` : ""}
          </span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-xs font-semibold text-gray-700 bg-transparent focus:outline-none"
          >
            <option value="popularity">Sort: Popularity</option>
            <option value="price_low">Sort: Price (low to high)</option>
          </select>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-2 gap-3">
            {filtered.flatMap((group) => {
              const { hotel, rooms } = group;
              if (!rooms || rooms.length === 0) return [];

              return rooms.map((room, ridx) => {
                const isThisRoomSelected =
                  hotel.hotel_name === currentHotelName &&
                  isSameRoom(room, currentRoom);
                const diff = Number(room.price_per_night || 0) - baseline;
                const priceLabel =
                  diff === 0
                    ? `₹${Number(room.price_per_night || 0).toLocaleString("en-IN")} · Base Price`
                    : `${diff > 0 ? "+" : "-"}₹${Math.abs(diff).toLocaleString("en-IN")}/night for upgrade`;

                return (
                  <HotelOptionCard
                    key={`${hotel.hotel_name}-${ridx}`}
                    hotel={hotel}
                    room={room}
                    isSelected={isThisRoomSelected}
                    isBaseline={diff === 0}
                    //priceLabel={priceLabel}
                    onSelect={(h, r) => {
                      onSelect(h, r);
                      onClose();
                    }}
                  />
                );
              });
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ───────────────────────── Transport selection ───────────────────────── */

const vehicleIcon = (vehicle = "") => {
  const v = vehicle.toLowerCase();
  if (v.includes("bus") || v.includes("tempo")) return "🚌";
  if (v.includes("suv") || v.includes("innova") || v.includes("ertiga"))
    return "🚙";
  return "🚗";
};

const priceOfTransport = (t) => {
  const usage = Number(t.usage_count) || 1;
  return (
    (t.type === "shared"
      ? Number(t.price_per_person || 0)
      : Number(t.price_per_vehicle || 0)) * usage
  );
};

const TransportOptionCard = ({
  t,
  isSelected,
  isBest,
  fits,
  totalGuests,
  onSelect,
}) => (
  <div
    className={`rounded-2xl border-2 overflow-hidden bg-white transition-colors relative
      ${!fits ? "opacity-40 border-gray-200" : isSelected ? "border-orange-500" : "border-gray-200 hover:border-gray-300"}`}
  >
    {isSelected && (
      <div className="absolute top-2 left-2 z-10 flex items-center gap-1 bg-orange-600 text-white text-[10px] font-bold px-2 py-1 rounded-full">
        <Check size={10} /> Selected
      </div>
    )}

    <div className="h-28 w-full overflow-hidden bg-gray-50">
      <ImageStrip
        variant="slider"
        type="transport"
        images={t.images}
        alt={t.vehicle}
      />
    </div>

    <div className="p-3">
      <h4 className="font-bold text-gray-900 text-sm leading-snug">
        {t.vehicle}
      </h4>

      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {isBest && !isSelected && (
          <span className="text-[10px] font-semibold px-2 py-1 rounded-md bg-amber-50 text-amber-800 border border-amber-200">
            Recommended
          </span>
        )}
        {!fits && (
          <span className="text-[10px] font-semibold px-2 py-1 rounded-md bg-red-50 text-red-700 border border-red-200">
            Too small
          </span>
        )}
      </div>

      <p className="text-[11px] text-gray-500 mt-2 flex items-center gap-1">
        <Users size={12} />
        Up to {t.capacity} pax ·{" "}
        {t.type === "shared"
          ? "Shared"
          : t.type === "flight"
            ? "Flight"
            : "Private"}
        {t.type === "flight" && t.flight_class
          ? ` · ${t.flight_class.replace("_", " ")}`
          : ""}
      </p>

      {t.leg_label && (
        <p className="text-[11px] text-gray-400 mt-1">{t.leg_label}</p>
      )}
      {t.notes && (
        <p className="text-[11px] text-gray-400 italic mt-1">{t.notes}</p>
      )}
      {!fits && (
        <p className="text-[11px] text-red-400 mt-1">
          Seats {t.capacity} · not enough for {totalGuests} guests
        </p>
      )}

      <button
        onClick={() => {
          if (fits) onSelect(t);
        }}
        disabled={!fits || isSelected}
        className={`mt-3 w-full text-xs font-bold py-2 rounded-lg transition-colors
          ${
            isSelected
              ? "bg-orange-100 text-orange-400 cursor-default"
              : fits
                ? "bg-slate-800 text-white hover:bg-slate-900"
                : "bg-gray-100 text-gray-300 cursor-not-allowed"
          }`}
      >
        {isSelected ? "Selected" : fits ? "Select" : "N/A"}
      </button>
    </div>
  </div>
);

const TransportSummaryCard = ({
  selection,
  autoSelected,
  allowNoTransport,
  onOpenPanel,
  onRemove,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  if (!selection) {
    return (
      <div className="border border-dashed border-gray-300 rounded-2xl p-4 flex items-center justify-between bg-white">
        <div>
          <p className="text-sm font-semibold text-gray-700">
            Self-arranged transport
          </p>
          <p className="text-xs text-gray-400">Not included in package</p>
        </div>
        <button
          onClick={onOpenPanel}
          className="text-sm font-semibold text-orange-600 hover:text-orange-700"
        >
          Add transfer
        </button>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white">
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-100"
      >
        <div className="flex items-center gap-2.5 text-gray-900">
          <span className="text-lg leading-none">
            {vehicleIcon(selection.vehicle)}
          </span>
          <span className="text-sm font-semibold tracking-tight">
            Transfer{selection.notes ? ` · ${selection.notes}` : ""}
          </span>
          {collapsed ? (
            <ChevronDown size={16} className="text-gray-400" />
          ) : (
            <ChevronUp size={16} className="text-gray-400" />
          )}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {allowNoTransport && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              className="text-sm font-semibold text-gray-500 hover:text-gray-700"
            >
              Remove
            </span>
          )}
          <span className="w-px h-4 bg-gray-200" />
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onOpenPanel();
            }}
            className="text-sm font-semibold text-orange-600 hover:text-orange-700"
          >
            Modify
          </span>
        </div>
      </button>

      {!collapsed && (
        <div className="p-4 flex items-start gap-4">
          <ImageStrip
            variant="square"
            type="transport"
            images={selection.images}
            alt={selection.vehicle}
          />

          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <h3 className="font-bold text-gray-900 text-[15px] leading-snug">
                {selection.vehicle}
              </h3>
              {autoSelected && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                  Recommended
                </span>
              )}
            </div>

            {selection.notes && (
              <p className="text-sm text-gray-600 mt-1">{selection.notes}</p>
            )}

            <p className="flex items-center gap-1 mt-3 text-xs text-gray-500">
              <Users size={12} /> Up to {selection.capacity} pax ·{" "}
              {selection.type === "shared"
                ? "Shared vehicle"
                : "Private vehicle"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const TransportOptionsPanel = ({
  transport,
  selectedTransport,
  totalGuests,
  onSelect,
  onClose,
}) => {
  const sorted = [...transport].sort(
    (a, b) => Number(a.capacity || 0) - Number(b.capacity || 0),
  );
  const fitting = transport.filter((t) => {
    const cap = Number(t.capacity || 0);
    return cap === 0 || cap >= totalGuests;
  });
  const bestFit = [...fitting].sort(
    (a, b) => priceOfTransport(a) - priceOfTransport(b),
  )[0];

  return (
    <div className="fixed inset-0 z-[999] flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative w-full sm:max-w-xl h-full bg-white flex flex-col shadow-2xl">
        <div className="px-5 pt-5 pb-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Transfer options</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-gray-100"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-2 gap-3">
            {sorted.map((t, idx) => {
              const capacity = Number(t.capacity || 0);
              const fits = capacity === 0 || capacity >= totalGuests;
              const isBest =
                fits &&
                bestFit?.vehicle === t.vehicle &&
                bestFit?.type === t.type;
              const isSel =
                selectedTransport?.vehicle === t.vehicle &&
                selectedTransport?.type === t.type;

              return (
                <TransportOptionCard
                  key={idx}
                  t={t}
                  isSelected={isSel}
                  isBest={isBest}
                  fits={fits}
                  totalGuests={totalGuests}
                  onSelect={(picked) => {
                    onSelect(picked);
                    onClose();
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ───────────────────────── Page ───────────────────────── */

const TourDetail = () => {
  const { id } = useParams();
  const bookingRef = useRef(null);
  const navigate = useNavigate();
  const [tour, setTour] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStickyBar, setShowStickyBar] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [travelDate, setTravelDate] = React.useState("");
  const [message, setMessage] = React.useState("");

  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [serviceTerms, setServiceTerms] = useState("");

  const [canReview, setCanReview] = useState(false);
  const [userBookings, setUserBookings] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 0, comment: "" });

  const [couponCode, setCouponCode] = useState("");
  const [couponData, setCouponData] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [couponError, setCouponError] = useState("");

  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);
  const [departures, setDepartures] = useState([]);
  const [selectedDeparture, setSelectedDeparture] = useState(null);

  // Per-stop hotel selection: { [stopKey]: { hotel, room } }
  const [selectedHotels, setSelectedHotels] = useState({});
  const [changeHotelStop, setChangeHotelStop] = useState(null);
  const [roomConfig, setRoomConfig] = useState([{ adults: 2, children: 0 }]);
  const [selectedTransport, setSelectedTransport] = useState(null);
  const [transportAutoSelected, setTransportAutoSelected] = useState(false);
  const [showTransportList, setShowTransportList] = useState(false);
  const [transportDismissed, setTransportDismissed] = useState(false);
  const [hiddenChargesPerPerson, setHiddenChargesPerPerson] = useState(0);
  const [pendingEventId, setPendingEventId] = useState(null);
  const [removedSightseeing, setRemovedSightseeing] = useState({});
  const sightseeingKey = (dayIdx, itemIdx) => `${dayIdx}-${itemIdx}`;

  // Old items without the flag count as removable
  const isItemRemovable = (item) =>
    item?.type !== "activity" || item.removable !== false;

  const isLoggedIn = () =>
    !!(
      localStorage.getItem("tokenUser") ||
      localStorage.getItem("tokenuser") ||
      localStorage.getItem("token")
    );

  // A non-removable item can never count as removed
  const isSightseeingRemoved = (dayIdx, itemIdx, item) =>
    isItemRemovable(item) &&
    !!removedSightseeing[sightseeingKey(dayIdx, itemIdx)];

  const toggleSightseeing = (dayIdx, itemIdx, item) => {
    if (!isItemRemovable(item)) return;
    if (!isLoggedIn()) {
      alert("Please log in to customise the activities in your itinerary.");
      // or: navigate('/login') / open your login modal
      return;
    }
    setRemovedSightseeing((prev) => {
      const key = sightseeingKey(dayIdx, itemIdx);
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = true;
      return next;
    });
  };

  function getMetaCookie(name) {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? match[2] : null;
  }

  function getFbc() {
    let fbc = getMetaCookie("_fbc");
    if (!fbc) {
      const params = new URLSearchParams(window.location.search);
      const fbclid = params.get("fbclid");
      if (fbclid) {
        fbc = `fb.1.${Date.now()}.${fbclid}`;
        document.cookie = `_fbc=${fbc}; path=/; max-age=${90 * 24 * 60 * 60}`;
      }
    }
    return fbc;
  }

  useEffect(() => {
    const fetchTour = async () => {
      try {
        setLoading(true);
        const fbp = getMetaCookie("_fbp");
        const fbc = getFbc();

        const res = await axios.get(`${KeyUri.BACKEND_URI}/get-tour/${id}`, {
          params: { fbp, fbc },
        });
        setTour(res.data);

        trackEvent(
          "ViewContent",
          {
            content_ids: [res.data._id],
            content_type: "tour_package",
            content_name: res.data.title,
            value: Number(res.data.display_price || res.data.price || 0),
            currency: "INR",
          },
          res.data.viewEventId,
        );

        if (res.data.is_group_tour) {
          const depRes = await axios.get(
            `${KeyUri.BACKEND_URI}/get-departures/${id}`,
          );
          const allDepartures = depRes.data?.data || [];

          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const upcoming = allDepartures.filter((dep) => {
            const start = new Date(dep.departure_date);
            start.setHours(0, 0, 0, 0);
            return start >= today;
          });

          setDepartures(upcoming);
        }
        const tourReviews = res.data.reviews || [];
        setReviews(tourReviews);
      } catch (err) {
        console.error("Failed to load tour:", err);
        setTour(null);
      } finally {
        setLoading(false);
      }
    };
    fetchTour();
  }, [id]);

  // Fetch TourPricing config for hidden platform/additional charges
  useEffect(() => {
    const fetchTourPricingConfig = async () => {
      try {
        const res = await axios.get(`${KeyUri.BACKEND_URI}/tourpricing`);
        const pricing = res.data?.data;
        const platformFee = Number(pricing?.platform_price || 0);
        const misc = Number(pricing?.miscellaneous_charges || 0);
        const addlOne = Number(pricing?.additional_charges_one || 0);
        const addlTwo = Number(pricing?.additional_charges_two || 0);
        setHiddenChargesPerPerson(platformFee + misc + addlOne + addlTwo);
        console.log("📥 [TourDetail] tourpricing:", pricing, "→ hidden/person =", platformFee + misc + addlOne + addlTwo);
      } catch (err) {
        console.error("Failed to fetch tour pricing config:", err);
      }
    };
    fetchTourPricingConfig();
  }, []);

  // Hide the mobile sticky bar while the booking card is on screen
  useEffect(() => {
    const handleScroll = () => {
      if (!bookingRef.current) return;
      const rect = bookingRef.current.getBoundingClientRect();
      const isBookingVisible =
        rect.top < window.innerHeight && rect.bottom >= 0;
      setShowStickyBar(!isBookingVisible);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleApplyCoupon = async () => {
    try {
      setCouponError("");

      const userId = JSON.parse(localStorage.getItem("userID"));
      const token = localStorage.getItem("token");

      if (!userId) {
        setCouponError("Please login to apply coupon");
        return;
      }

      const res = await axios.post(
        `${KeyUri.BACKEND_URI}/tour-coupon/validate`,
        {
          coupon_code: couponCode,
          tourId: id,
          bookingAmount: Number(tour.display_price || tour.price || 0),
          tourDate: travelDate,
          userId: userId,
          participants: Number(travellerCount),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = res.data;

      setCouponData(data.coupon);
      setDiscountAmount(data.discount);
    } catch (err) {
      setCouponError(err.response?.data?.message || "Invalid coupon");
      setCouponData(null);
      setDiscountAmount(0);
    }
  };

  const fetchAvailableCoupons = async () => {
    try {
      setCouponLoading(true);

      const res = await axios.get(
        `${KeyUri.BACKEND_URI}/get-tour-coupon/public`,
      );

      let coupons = res.data?.coupons || res.data?.data || res.data || [];
      if (!Array.isArray(coupons)) coupons = [];

      const today = new Date();

      const filtered = coupons.filter((c) => {
        if (!c.is_active) return false;
        if (c.valid_from && new Date(c.valid_from) > today) return false;
        if (c.valid_until && new Date(c.valid_until) < today) return false;
        if (c.usage_limit && c.used_count >= c.usage_limit) return false;
        if (c.applicable_tours?.length) {
          return c.applicable_tours.includes(id);
        }
        return true;
      });

      filtered.sort((a, b) => (b.coupon_amount || 0) - (a.coupon_amount || 0));

      setAvailableCoupons(filtered);
      setCouponModalOpen(true);
    } catch (err) {
      console.error("Coupon fetch error:", err);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleSelectCoupon = async (coupon) => {
    setCouponCode(coupon.coupon_code);
    setCouponModalOpen(false);

    try {
      const userId = JSON.parse(localStorage.getItem("userID"));

      const res = await axios.post(
        `${KeyUri.BACKEND_URI}/tour-coupon/validate`,
        {
          coupon_code: coupon.coupon_code,
          tourId: id,
          bookingAmount: Number(tour.display_price || tour.price || 0),
          tourDate: travelDate,
          userId: userId,
          participants: Number(travellerCount),
        },
      );

      setCouponData(res.data.coupon);
      setDiscountAmount(res.data.discount);
      setCouponError("");
    } catch (err) {
      setCouponError(err.response?.data?.message || "Invalid coupon");
      setCouponData(null);
      setDiscountAmount(0);
    }
  };

  useEffect(() => {
    const fetchServiceTerms = async () => {
      try {
        const res = await axios.get(
          `${KeyUri.BACKEND_URI}/terms/service-user-terms`,
        );
        setServiceTerms(res.data.serviceUserTerms || "");
      } catch (error) {
        console.error("Failed to fetch terms:", error);
      }
    };
    fetchServiceTerms();
  }, []);

  useEffect(() => {
    const checkUserBookings = async () => {
      try {
        const tokenUser =
          localStorage.getItem("tokenUser") ||
          localStorage.getItem("tokenuser") ||
          localStorage.getItem("token");
        if (!tokenUser) {
          setCanReview(false);
          return;
        }

        const token = tokenUser.replace(/^"|"$/g, "");
        const userId =
          JSON.parse(atob(token.split(".")[1]))?.id ||
          JSON.parse(atob(token.split(".")[1]))?._id;

        if (!userId) {
          setCanReview(false);
          return;
        }

        const res = await axios.get(
          `${KeyUri.BACKEND_URI}/tour-booking/${userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const tourBookings =
          res.data.data?.filter((booking) => {
            const matchesTour = booking.tourId?._id?.toString() === id;
            const isConfirmedOrCompleted = ["confirmed", "completed"].includes(
              booking.status,
            );
            const isPastOrToday = new Date(booking.date) <= new Date();
            return matchesTour && isConfirmedOrCompleted && isPastOrToday;
          }) || [];

        setUserBookings(tourBookings);
        setCanReview(tourBookings.length > 0);
      } catch (err) {
        console.error(
          " Booking check failed:",
          err.response?.status,
          err.message,
        );
        setCanReview(false);
      }
    };

    if (tour && id) checkUserBookings();
  }, [id, tour]);

  // Aggregate room-capacity constraint: the strictest max_occupancy among all
  // currently-selected rooms across every stop.
  const selectedRoomsList = Object.values(selectedHotels)
    .map((sh) => sh.room)
    .filter(Boolean);
  const hasAnyHotelSelected = selectedRoomsList.length > 0;
  const maxOccupancy = hasAnyHotelSelected
    ? Math.min(...selectedRoomsList.map((r) => Number(r.max_occupancy) || 2))
    : 2;
  const totalRooms = roomConfig.length;
  const totalGuests = roomConfig.reduce(
    (sum, r) => sum + r.adults + r.children,
    0,
  );

  const totalAdults = roomConfig.reduce((sum, r) => sum + r.adults, 0);
  const totalChildren = roomConfig.reduce((sum, r) => sum + r.children, 0);

  // Auto-select cheapest hotel+room PER STOP when accommodation is mandatory
  useEffect(() => {
    if (!tour || tour.stay_policy === "not_available") return;
    if (!Array.isArray(tour.hotels) || tour.hotels.length === 0) return;

    const stops = buildStopGroups(tour.hotels);

    setSelectedHotels((prev) => {
      let changed = false;
      const next = { ...prev };

      stops.forEach((stop) => {
        if (next[stop.key]) return;

        let cheapest = null;
        stop.hotelGroups.forEach(({ hotel, rooms }) => {
          rooms.forEach((room) => {
            const price = Number(room.price_per_night || 0);
            if (
              !cheapest ||
              price < Number(cheapest.room.price_per_night || 0)
            ) {
              cheapest = { hotel, room };
            }
          });
        });

        if (cheapest) {
          next[stop.key] = cheapest;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [tour]);

  useEffect(() => {
    if (!tour || tour.travel_policy === "not_required") return;
    if (selectedTransport) return;
    if (transportDismissed) return;
    if (!Array.isArray(tour.transport) || tour.transport.length === 0) return;

    const priceOf = (t) =>
      t.type === "shared"
        ? Number(t.price_per_person || 0)
        : Number(t.price_per_vehicle || 0);

    const fitting = tour.transport.filter((t) => {
      const capacity = Number(t.capacity || 0);
      return capacity === 0 || capacity >= totalGuests;
    });

    const cheapestFit = [...fitting].sort((a, b) => priceOf(a) - priceOf(b))[0];
    if (cheapestFit) {
      setSelectedTransport(cheapestFit);
      setTransportAutoSelected(true);
    }
  }, [tour, totalGuests, transportDismissed]);

  useEffect(() => {
    if (!Array.isArray(tour?.transport) || tour.transport.length === 0) return;
    if (selectedTransport === null) return;

    const currentCapacity = Number(selectedTransport.capacity || 0);
    const currentFits = currentCapacity === 0 || currentCapacity >= totalGuests;
    if (currentFits) return;

    // current cab is now too small → upgrade to smallest that fits
    const sorted = [...tour.transport].sort(
      (a, b) => Number(a.capacity || 0) - Number(b.capacity || 0),
    );
    const nextFit = sorted.find(
      (t) =>
        Number(t.capacity || 0) === 0 || Number(t.capacity || 0) >= totalGuests,
    );
    if (nextFit) {
      setSelectedTransport(nextFit);
      setTransportAutoSelected(true);
    }
  }, [totalGuests, tour]);

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (newReview.rating < 1 || !newReview.comment.trim()) {
      alert("Please provide rating and comment");
      return;
    }

    try {
      const bookingId = userBookings[0]._id;
      await axios.post(
        `${KeyUri.BACKEND_URI}/tours/${id}/reviews`,
        {
          rating: newReview.rating,
          comment: newReview.comment,
          bookingId,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );

      alert("Review submitted successfully!");
      setShowReviewForm(false);
      setNewReview({ rating: 0, comment: "" });

      const res = await axios.get(`${KeyUri.BACKEND_URI}/tours/${id}/reviews`);
      setReviews(res.data.reviews);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to submit review");
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );

  if (!tour)
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-xl text-gray-600">Tour not found</p>
      </div>
    );

  const avgRating =
    reviews.length > 0
      ? reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length
      : 0;
  const reviewCount = reviews.length || tour?.numReviews || 0;

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      alert("Name is required");
      return;
    }

    if (!/^[A-Za-z\s]+$/.test(name)) {
      alert("Name should contain only alphabets");
      return;
    }

    if (!/^\d{10}$/.test(phone)) {
      alert("Phone must be exactly 10 digits");
      return;
    }

    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      alert("Enter valid email");
      return;
    }

    if (travelDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (new Date(travelDate) < today) {
        alert("Past dates are not allowed");
        return;
      }
    }

    try {
      const response = await fetch(KeyUri.BACKEND_URI + `/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          phone,
          email,
          tour: tour.title,
          travelDate: travelDate || "Not specified",
          travellerCount: travellerCount || 1,
          message,
          fbp: getMetaCookie("_fbp"),
          fbc: getFbc(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit lead");
      }

      const data = await response.json();
      alert(data.message || "Thank you! Your details have been recorded.");

      trackEvent(
        "Lead",
        {
          content_name: "tour_enquiry",
          content_category: tour.title,
          value: 0,
          currency: "INR",
        },
        data.eventId,
      );

      setName("");
      setPhone("");
      setEmail("");
      setSubmitSuccess(true);
    } catch (error) {
      alert(error.message);
    }
  };

  // Per-stop groups derived from tour.hotels (grouped by location_label)
  const stopGroups = buildStopGroups(tour.hotels);

  const kidsDiscountPct = tour?.kids_discount_percentage || 0;
  // Base price folds in the hidden platform/additional charges — nothing is ever shown as a separate fee
  const basePricePerPerson =
    Number(tour.display_price || tour.price || 0) + hiddenChargesPerPerson;
  const kidsPricePerPerson = Math.round(
    basePricePerPerson * (1 - kidsDiscountPct / 100),
  );

  const getRoomTotal = (room) => room.adults + room.children;
  const isRoomOverCapacity = (room) => getRoomTotal(room) > maxOccupancy;
  const anyRoomOverCapacity =
    hasAnyHotelSelected && roomConfig.some(isRoomOverCapacity);
  const travellerCount = totalGuests;

  const addRoom = () =>
    setRoomConfig((prev) => [...prev, { adults: 1, children: 0 }]);
  const removeRoom = (idx) =>
    setRoomConfig((prev) => prev.filter((_, i) => i !== idx));
  const updateRoom = (idx, field, val) =>
    setRoomConfig((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [field]: Math.max(0, val) } : r)),
    );

  const hotelAddonTotal = stopGroups.reduce((sum, stop) => {
    const sel = selectedHotels[stop.key];
    if (!sel) return sum;
    const stopNights = Number(sel.hotel.nights) || 1;
    return (
      sum + Number(sel.room.price_per_night || 0) * stopNights * totalRooms
    );
  }, 0);

  const transportUsageCount = Number(selectedTransport?.usage_count) || 1;

  const transportAddon = selectedTransport
    ? (selectedTransport.type === "shared"
        ? Number(selectedTransport.price_per_person || 0)
        : Number(selectedTransport.price_per_vehicle || 0)) *
      transportUsageCount
    : 0;

  const transportAddonTotal = selectedTransport
    ? selectedTransport.type === "shared"
      ? transportAddon * totalGuests
      : transportAddon
    : 0;

  const selectedSightseeingItems = (tour.itinerary || []).flatMap(
    (day, dayIdx) =>
      (day.sightseeing || [])
        .map((item, itemIdx) => ({ ...item, dayIdx, itemIdx }))
        .filter(
          (item) =>
            !isSightseeingRemoved(item.dayIdx, item.itemIdx, item) &&
            item.price > 0,
        ),
  );
  const sightseeingAddonTotal = selectedSightseeingItems.reduce(
    (sum, item) => sum + Number(item.price || 0) * totalGuests,
    0,
  );

  const effectivePrice =
    totalGuests > 0
      ? basePricePerPerson +
        Math.round(
          (hotelAddonTotal + transportAddonTotal + sightseeingAddonTotal) /
            totalGuests,
        )
      : basePricePerPerson +
        hotelAddonTotal +
        transportAddonTotal +
        sightseeingAddonTotal;

        console.group("🧮 [TourDetail] price calculation");
console.log("guests / rooms:", { totalGuests, totalRooms });
console.log("base price:", Number(tour.display_price || tour.price || 0), "+ hidden:", hiddenChargesPerPerson, "= basePricePerPerson:", basePricePerPerson);

console.table(
  stopGroups.map((stop) => {
    const sel = selectedHotels[stop.key];
    const nights = Number(sel?.hotel?.nights) || 1;
    const rate = Number(sel?.room?.price_per_night || 0);
    return {
      stop: stop.label || stop.key,
      hotel: sel?.hotel?.hotel_name,
      room: sel?.room?.room_name,
      rawNights: sel?.hotel?.nights,   // undefined = schema dropped it
      nightsUsed: nights,
      rate,
      rooms: totalRooms,
      subtotal: rate * nights * totalRooms,
    };
  }),
);
console.log("hotelAddonTotal:", hotelAddonTotal);

console.log("selectedTransport:", selectedTransport);
console.log("transport:", {
  type: selectedTransport?.type,
  per_person: selectedTransport?.price_per_person,
  per_vehicle: selectedTransport?.price_per_vehicle,
  usage: transportUsageCount,
  transportAddon,
  transportAddonTotal,
});
console.log("sightseeingAddonTotal:", sightseeingAddonTotal);

console.log(
  "effectivePrice =",
  basePricePerPerson,
  "+ round((",
  hotelAddonTotal, "+", transportAddonTotal, "+", sightseeingAddonTotal,
  ") /", totalGuests, ") =", effectivePrice,
);
console.groupEnd();

  const originalPrice = Number(tour.original_price || 0);
  const hasDiscount = originalPrice > effectivePrice;
  const saveAmount = hasDiscount ? originalPrice - effectivePrice : 0;
  const savePercent = hasDiscount
    ? Math.round((saveAmount / originalPrice) * 100)
    : 0;

  const selectedHotelsForBooking = stopGroups
    .map((stop) => {
      const sel = selectedHotels[stop.key];
      if (!sel) return null;
      return {
        location_label: stop.label,
        hotel: sel.hotel,
        room: sel.room,
        nights: Number(sel.hotel.nights) || 1,
      };
    })
    .filter(Boolean);

  const isEmbedded =
    new URLSearchParams(window.location.search).get("embed") === "true";

  const goToBookingCard = () =>
    document
      .getElementById("booking-card")
      ?.scrollIntoView({ behavior: "smooth" });

  // Shared by the booking card button and the mobile sticky bar
  const handleBookNow = () => {
    if (tour.is_group_tour && !selectedDeparture) {
      alert("Please select a departure date to continue.");
      return goToBookingCard();
    }
    if (!tour.is_group_tour && !travelDate) {
      alert("Please select a travel date to continue.");
      return goToBookingCard();
    }
    if (anyRoomOverCapacity) {
      alert(
        `Max ${maxOccupancy} guests allowed per room. Please add another room.`,
      );
      return goToBookingCard();
    }
    setTermsModalOpen(true);
  };

  const todayISO = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .split("T")[0];

  // Inquiry form fields — rendered inline in the card (desktop) and in a bottom sheet (mobile)
  const inquiryForm = (
    <>
      {submitSuccess && (
        <p className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg text-sm">
          Thank you! Your details have been recorded.
        </p>
      )}
      <form onSubmit={handleContactSubmit} className="space-y-3">
        <TextField
          label="Name"
          variant="outlined"
          fullWidth
          required
          size="small"
          value={name}
          onChange={(e) => {
            const value = e.target.value;
            if (/^[A-Za-z\s]*$/.test(value)) setName(value);
          }}
        />
        <TextField
          label="Phone Number"
          variant="outlined"
          fullWidth
          required
          size="small"
          type="tel"
          value={phone}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, "");
            if (value.length <= 10) setPhone(value);
          }}
          inputProps={{ maxLength: 10 }}
        />
        <TextField
          label="Email"
          variant="outlined"
          fullWidth
          required
          size="small"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          label="Preferred Travel Date"
          variant="outlined"
          fullWidth
          size="small"
          type="date"
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: todayISO }}
          value={travelDate}
          onChange={(e) => setTravelDate(e.target.value)}
        />
        <TextField
          label="Number of Travellers"
          variant="outlined"
          fullWidth
          size="small"
          type="number"
          value={travellerCount}
          InputProps={{ readOnly: true }}
          helperText="Based on room configuration"
        />
        <TextField
          label="Message"
          variant="outlined"
          fullWidth
          multiline
          rows={3}
          size="small"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Questions about dates, customization, group rates?"
        />
        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{ textTransform: "none", fontWeight: "bold" }}
        >
          Send Inquiry
        </Button>
      </form>
    </>
  );

  const highlights = Array.isArray(tour.about)
  ? tour.about.filter((p) => typeof p === "string" && p.trim())
  : [];

  return (
    <div
      className={`min-h-screen bg-slate-50 ${isEmbedded ? "" : "pb-24"} lg:pb-0 overflow-x-clip`}
    >
      {/* Back Button */}
      {!isEmbedded ? (
        <div className="max-w-7xl mx-auto px-2 md:ml-8 pt-24 md:pt-40">
          <button
            onClick={() => {
              if (window.history.length > 1) navigate(-1);
              else navigate("/tours");
            }}
            className="flex items-center gap-2 text-orange-700 font-medium hover:text-orange-900 transition"
          >
            ← Back
          </button>
        </div>
      ) : (
        <div className="pt-2" />
      )}

      {/* Main layout: left = story (hero image first), right = booking card beside the image */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-4 pb-8 mt-2">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 min-w-0">
          <div className="lg:col-span-7 xl:col-span-8 space-y-8 lg:space-y-12 min-w-0">
            {/* 1. HERO */}
            <section id="hero">
              <TourDetailImageGallery
                images={tour.image_url}
                title={tour.title}
                overlay={
                  <HeroMeta
                    tour={tour}
                    avgRating={avgRating}
                    reviewCount={reviewCount}
                    onImage
                  />
                }
                fallback={
                  <HeroMeta
                    tour={tour}
                    avgRating={avgRating}
                    reviewCount={reviewCount}
                  />
                }
              />

              {/* Mobile-only title block below the image */}
              <div className="md:hidden mt-6">
                <HeroMeta
                  tour={tour}
                  avgRating={avgRating}
                  reviewCount={reviewCount}
                />
              </div>
            </section>

            {/* 2. Overview */}
            {tour.description && (
              <div className={CARD}>
                <h2 className={`${H2} mb-6`}>About This Tour</h2>
                <div
                  className="text-gray-700 leading-relaxed prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: tour.description }}
                />
              </div>
            )}

{/* 3. Highlights */}
{highlights.length > 0 && (
  <div className={CARD}>
    <h2 className={`${H2} mb-8`}>Highlights</h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8">
      {highlights.map((point, index) => (
        <div key={index} className="flex gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center text-orange-600 shadow-sm">
            <CheckCircle size={22} />
          </div>
          <p className="text-slate-700 text-sm leading-relaxed self-center">
            {point}
          </p>
        </div>
      ))}
    </div>
  </div>
)}
            {/* 4. Itinerary */}
            {Array.isArray(tour.itinerary) && tour.itinerary.length > 0 && (
              <section id="itinerary">
                <h2 className={`${H2} mb-8`}>Detailed Itinerary</h2>
                <div className="space-y-6">
                  {tour.itinerary.map((day, idx) => (
                    <details
                      key={idx}
                      open={idx === 0}
                      className="group bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <summary className="flex items-center justify-between gap-4 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                        <div className="flex items-center gap-4 min-w-0">
                          <div
                            className="w-12 h-12 flex-shrink-0 rounded-full flex items-center justify-center font-serif font-bold text-lg
                              bg-orange-100 text-orange-700 border border-orange-200
                              group-open:bg-orange-600 group-open:text-white group-open:border-orange-600
                              transition-colors"
                          >
                            {day.day}
                          </div>
                          <h3 className="font-bold text-lg text-slate-800">
                            {day.title}
                          </h3>
                        </div>
                        <svg
                          className="w-5 h-5 flex-shrink-0 text-slate-400 transition-transform group-open:rotate-180"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </summary>

                      <div className="mt-4 sm:pl-16 text-slate-600 text-sm leading-relaxed">
                        <div
                          className="prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: day.details }}
                        />

                        {Array.isArray(day.sightseeing) &&
                          day.sightseeing.length > 0 && (
                            <div className="mt-4 pt-3 border-t border-gray-100">
                              <p className="font-semibold text-slate-800 mb-2">
                                Places & activities included this day:
                              </p>
                              <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
                                {day.sightseeing.map((item, itemIdx) => {
                                  const removed = isSightseeingRemoved(
                                    idx,
                                    itemIdx,
                                    item,
                                  );
                                  return (
                                    <div
                                      key={itemIdx}
                                      className={`flex-shrink-0 w-40 rounded-xl border overflow-hidden transition-opacity ${removed ? "opacity-40 border-gray-200" : "border-orange-200"}`}
                                    >
                                      <div className="h-20 w-full bg-gray-100">
                                        {item.image && (
                                          <img
                                            src={item.image}
                                            alt={item.name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                              e.target.style.display = "none";
                                            }}
                                          />
                                        )}
                                      </div>
                                      <div className="p-2">
                                        <div className="flex items-start justify-between gap-1">
                                          <p className="text-xs font-semibold text-gray-900 leading-snug line-clamp-2">
                                            {item.name}
                                          </p>
                                          {isItemRemovable(item) ? (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                toggleSightseeing(
                                                  idx,
                                                  itemIdx,
                                                  item,
                                                )
                                              }
                                              className="flex-shrink-0 text-gray-400 hover:text-gray-700"
                                              title={
                                                removed ? "Add back" : "Remove"
                                              }
                                            >
                                              {removed ? "+" : <X size={14} />}
                                            </button>
                                          ) : (
                                            <span className="flex-shrink-0 text-[9px] font-semibold text-orange-700 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded">
                                              Included
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-[10px] text-gray-400 mt-0.5">
                                          {item.type === "activity"
                                            ? "Activity"
                                            : "Place"}
                                          {item.location
                                            ? ` · ${item.location}`
                                            : ""}
                                        </p>
                                        {item.price > 0 && (
                                          <p
                                            className={`text-xs font-semibold mt-1 ${removed ? "text-gray-400 line-through" : "text-orange-600"}`}
                                          >
                                            +₹
                                            {Number(item.price).toLocaleString(
                                              "en-IN",
                                            )}
                                            /person
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            )}

            {/* 5. Hotels */}
            {tour.stay_policy !== "not_available" && stopGroups.length > 0 && (
              <div id="hotels" className={CARD}>
                <h2 className={`${H2} mb-4`}>Choose Accommodation</h2>
                <div className="space-y-4">
                  {stopGroups.map((stop) => (
                    <HotelSummaryCard
                      key={stop.key}
                      stop={stop}
                      selection={selectedHotels[stop.key] || null}
                      totalRooms={totalRooms}
                      totalGuests={totalGuests}
                      allowNoHotel={tour.stay_policy !== "mandatory"}
                      onOpenPanel={() => setChangeHotelStop(stop)}
                      onClearHotel={() => {
                        setSelectedHotels((prev) => {
                          const next = { ...prev };
                          delete next[stop.key];
                          return next;
                        });
                        setRoomConfig([{ adults: 2, children: 0 }]);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* 6. Transport */}
            {tour.travel_policy !== "not_required" &&
              Array.isArray(tour.transport) &&
              tour.transport.length > 0 && (
                <div id="transport" className={CARD}>
                  <div className="flex items-center gap-3 mb-4">
                    <Truck className="text-orange-600" size={24} />
                    <h2 className={H2}>Transport Options</h2>
                  </div>

                  {tour.travel_policy === "mandatory" && (
                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 mb-4">
                      Transport is included as part of this tour.
                    </p>
                  )}

                  <TransportSummaryCard
                    selection={selectedTransport}
                    autoSelected={transportAutoSelected}
                    allowNoTransport={tour.travel_policy !== "mandatory"}
                    onOpenPanel={() => setShowTransportList(true)}
                    onRemove={() => {
                      setSelectedTransport(null);
                      setTransportAutoSelected(false);
                      setTransportDismissed(true);
                    }}
                  />
                </div>
              )}

            {/* 7. Inclusions and Exclusions */}
            {((Array.isArray(tour.inclusions) && tour.inclusions.length > 0) ||
              (Array.isArray(tour.exclusions) &&
                tour.exclusions.length > 0)) && (
              <div className="grid md:grid-cols-2 gap-6">
                {Array.isArray(tour.inclusions) &&
                  tour.inclusions.length > 0 && (
                    <div className={CARD}>
                      <div className="flex items-center gap-2 mb-4">
                        <CheckCircle className="text-green-600" size={24} />
                        <h2 className="text-xl font-serif font-bold text-slate-900">
                          What's Included
                        </h2>
                      </div>
                      <ul className="space-y-3">
                        {tour.inclusions.map((item, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <CheckCircle
                              className="text-green-500 flex-shrink-0 mt-1"
                              size={18}
                            />
                            <span className="text-gray-700 text-sm">
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                {Array.isArray(tour.exclusions) &&
                  tour.exclusions.length > 0 && (
                    <div className={CARD}>
                      <div className="flex items-center gap-2 mb-4">
                        <XCircle className="text-red-600" size={24} />
                        <h2 className="text-xl font-serif font-bold text-slate-900">
                          What's Not Included
                        </h2>
                      </div>
                      <ul className="space-y-3">
                        {tour.exclusions.map((item, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <XCircle
                              className="text-red-500 flex-shrink-0 mt-1"
                              size={18}
                            />
                            <span className="text-gray-700 text-sm">
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </div>
            )}

            {/* 8. Policy */}
            <div className={CARD}>
              <div className="flex items-center gap-3 mb-4">
                <FileText className="text-orange-600" size={24} />
                <h2 className={H2}>Tour Policy</h2>
              </div>
              {Array.isArray(tour.policy) && tour.policy.length > 0 ? (
                <ul className="space-y-3">
                  {tour.policy.map((item, index) => (
                    <li key={index} className="text-gray-700 text-sm italic">
                      {item}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 italic text-sm">
                  No specific policies for this tour.
                </p>
              )}
            </div>

            {/* 9. Reviews */}
            {/*  <div className={CARD}>
              <div className="flex items-center justify-between mb-6">
                <h2 className={H2}>Reviews ({reviewCount})</h2>
                {tour?.avgRating > 0 && (
                  <div className="flex items-center gap-1">
                    <Star size={20} className="fill-orange-400 text-orange-400" />
                    <span className="font-semibold text-lg">{tour.avgRating}</span>
                  </div>
                )}
              </div>

              {reviews.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Star size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-lg">No reviews yet</p>
                  <p>Be the first to share your tour experience!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {reviews.slice(0, 3).map((review, index) => (
                    <div key={review._id || review.id || index} className="p-4 bg-gray-50 rounded-xl border-l-4 border-orange-500">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-gray-800">
                            {review.userId?.name ||
                             review.userId?.firstName ||
                             review.userId?.username ||
                             review.user_name ||
                             review.user?.name ||
                             `Guest ${index + 1}`}
                          </p>
                          <div className="flex items-center gap-1 mt-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                size={16}
                                className={i < (review.rating || 0) ? "fill-orange-500 text-orange-500" : "text-gray-300"}
                              />
                            ))}
                          </div>
                        </div>

                        <span className="text-sm text-gray-500">
                          {new Date(review.createdAt || review.created_at || review.createdAtDate || Date.now())
                            .toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                      <p className="text-gray-700 mt-2">{review.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </div> */}
          </div>

          {/* ================= RIGHT COLUMN: booking card (beside the hero image on desktop) ================= */}
          <div
            id="booking-card"
            ref={bookingRef}
            className="lg:col-span-5 xl:col-span-4 self-start lg:sticky lg:top-24 z-30 min-w-0 scroll-mt-24"
          >
            <div
              className="bg-white rounded-3xl p-5 lg:p-8 shadow-[0_20px_50px_-12px_rgb(0,0,0,0.1)] border border-gray-200 lg:max-h-[calc(90vh-7rem)] lg:overflow-y-auto lg:pr-1   [scrollbar-width:thin] [scrollbar-color:#d1d5db_transparent]
  [&::-webkit-scrollbar]:w-1.5
  [&::-webkit-scrollbar-track]:bg-transparent
  [&::-webkit-scrollbar-thumb]:rounded-full
  [&::-webkit-scrollbar-thumb]:bg-gray-300
  hover:[&::-webkit-scrollbar-thumb]:bg-gray-400"
            >
              {/* Price Section */}
              <div className="mb-6 pb-6 border-b border-gray-100">
                <p className="text-sm text-slate-500 font-medium mb-1 flex items-center gap-2">
                  <span>Starting from</span>
                  <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded-sm">
                    excl. all taxes
                  </span>
                </p>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-serif font-bold text-slate-900">
                    ₹{effectivePrice.toLocaleString("en-IN")}
                  </span>
                  <span className="text-sm text-slate-500">/adult</span>
                </div>

                {hasDiscount && (
                  <div className="flex items-center gap-2 flex-wrap mt-2">
                    <span className="text-sm text-gray-400 line-through">
                      ₹{originalPrice.toLocaleString("en-IN")}
                    </span>
                    <span className="inline-block bg-green-50 text-green-700 px-2 py-1 rounded text-xs font-bold border border-green-200">
                      SAVE ₹{saveAmount.toLocaleString("en-IN")} ({savePercent}%
                      OFF)
                    </span>
                  </div>
                )}
              </div>

              {/* Date / departure */}
              <div className="mb-5">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                  <Calendar size={16} />
                  {tour.is_group_tour ? "Select Departure Date" : "Travel Date"}
                </label>

                {tour.is_group_tour ? (
                  departures.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">
                      No upcoming departures available.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {departures.map((dep) => (
                        <div
                          key={dep._id}
                          onClick={() => {
                            if (dep.status === "full") return;
                            setSelectedDeparture(dep);
                            setTravelDate(
                              new Date(dep.departure_date)
                                .toISOString()
                                .split("T")[0],
                            );
                          }}
                          className={`w-full border-2 rounded-xl p-3 transition
                            ${
                              dep.status === "full"
                                ? "opacity-50 cursor-not-allowed border-gray-200 bg-gray-50"
                                : selectedDeparture?._id === dep._id
                                  ? "border-orange-500 bg-orange-50 cursor-pointer"
                                  : "border-gray-200 hover:border-orange-300 cursor-pointer"
                            }`}
                        >
                          <div className="flex justify-between items-center gap-2">
                            <div>
                              <p className="font-bold text-slate-800 text-sm">
                                {new Date(
                                  dep.departure_date,
                                ).toLocaleDateString("en-IN", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                })}
                                {" → "}
                                {new Date(dep.return_date).toLocaleDateString(
                                  "en-IN",
                                  {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  },
                                )}
                              </p>
                              {dep.show_slots_on_user_side !== false && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {dep.available_seats ??
                                    dep.total_seats - dep.booked_seats}{" "}
                                  seats left
                                </p>
                              )}
                            </div>

                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span
                                className={`text-xs font-semibold px-2 py-1 rounded-full
                                ${
                                  dep.status === "open"
                                    ? "bg-green-100 text-green-700"
                                    : dep.status === "filling_fast"
                                      ? "bg-orange-100 text-orange-700"
                                      : "bg-red-100 text-red-700"
                                }`}
                              >
                                {dep.status === "open"
                                  ? "Available"
                                  : dep.status === "filling_fast"
                                    ? "Filling Fast"
                                    : "Full"}
                              </span>
                              {selectedDeparture?._id === dep._id && (
                                <CheckCircle
                                  className="text-orange-500"
                                  size={20}
                                />
                              )}
                            </div>
                          </div>

                          {dep.price_override && (
                            <p className="text-xs text-orange-600 font-semibold mt-1">
                              Special price: ₹
                              {dep.price_override.toLocaleString("en-IN")}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  <TextField
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: todayISO }}
                    value={travelDate}
                    onChange={(e) => setTravelDate(e.target.value)}
                  />
                )}
              </div>

              {/* Rooms & guests */}
              <div className="mb-4">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                  <Users size={16} />
                  Rooms & Guests
                </label>

                <div className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-gray-50 mb-3">
                  {totalRooms} Room{totalRooms > 1 ? "s" : ""} · {totalGuests}{" "}
                  Guest{totalGuests > 1 ? "s" : ""}
                  {hasAnyHotelSelected && (
                    <span className="text-xs text-gray-400 ml-1">
                      (max {maxOccupancy}/room)
                    </span>
                  )}
                </div>

                <div className="space-y-3">
                  {roomConfig.map((room, idx) => (
                    <div
                      key={idx}
                      className="border border-gray-200 rounded-2xl p-3"
                    >
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Room {idx + 1}
                        </span>
                        {roomConfig.length > 1 && (
                          <button
                            onClick={() => removeRoom(idx)}
                            className="text-xs text-red-500 font-semibold hover:underline"
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            Adults
                          </p>
                          <p className="text-xs text-gray-400">
                            Above 12 Years
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() =>
                              updateRoom(idx, "adults", room.adults - 1)
                            }
                            disabled={room.adults <= 1}
                            className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-orange-500 hover:text-orange-500 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            −
                          </button>
                          <span className="w-6 text-center text-sm font-semibold">
                            {String(room.adults).padStart(2, "0")}
                          </span>
                          <button
                            onClick={() =>
                              updateRoom(idx, "adults", room.adults + 1)
                            }
                            disabled={
                              hasAnyHotelSelected &&
                              getRoomTotal(room) >= maxOccupancy
                            }
                            className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-orange-500 hover:text-orange-500 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            Children
                          </p>
                          <p className="text-xs text-gray-400">
                            Below 12 Years
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() =>
                              updateRoom(idx, "children", room.children - 1)
                            }
                            disabled={room.children <= 0}
                            className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-orange-500 hover:text-orange-500 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            −
                          </button>
                          <span className="w-6 text-center text-sm font-semibold">
                            {String(room.children).padStart(2, "0")}
                          </span>
                          <button
                            onClick={() =>
                              updateRoom(idx, "children", room.children + 1)
                            }
                            disabled={
                              hasAnyHotelSelected &&
                              getRoomTotal(room) >= maxOccupancy
                            }
                            className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-orange-500 hover:text-orange-500 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div className="flex justify-between items-center mt-2">
                        <span className="text-xs text-gray-400">
                          {getRoomTotal(room)}
                          {hasAnyHotelSelected ? ` / ${maxOccupancy}` : ""}{" "}
                          guests
                        </span>
                        {hasAnyHotelSelected && isRoomOverCapacity(room) && (
                          <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                            Exceeds max occupancy
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {totalChildren > 0 && kidsDiscountPct > 0 && (
                  <p className="text-xs text-green-600 mt-2">
                    {totalChildren} child{totalChildren > 1 ? "ren" : ""} get{" "}
                    {kidsDiscountPct}% off
                  </p>
                )}

                {hasAnyHotelSelected &&
                  totalGuests > totalRooms * maxOccupancy && (
                    <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 mt-2">
                      {totalGuests} guests · max {maxOccupancy}/room — consider
                      adding a room
                    </p>
                  )}

                <button
                  onClick={addRoom}
                  className="mt-2 w-full border border-dashed border-orange-400 text-orange-600 text-sm font-semibold py-2 rounded-xl hover:bg-orange-50 transition"
                >
                  + Add Another Room
                </button>
              </div>

              {/* Coupon */}
              <div className="flex justify-end mb-2">
                <button
                  onClick={fetchAvailableCoupons}
                  className="text-orange-600 text-sm font-semibold hover:underline"
                >
                  View Available Coupons
                </button>
              </div>

              <div className="mb-4">
                <TextField
                  label="Apply Coupon"
                  fullWidth
                  size="small"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  error={!!couponError}
                  helperText={couponError || undefined}
                />
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{ mt: 1 }}
                  onClick={handleApplyCoupon}
                >
                  Apply Coupon
                </Button>

                {couponData && (
                  <p className="text-green-600 text-sm mt-2 font-medium">
                    Coupon applied! You saved ₹{discountAmount}
                  </p>
                )}
              </div>

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleBookNow}
                sx={{
                  mt: 1,
                  py: 1.75,
                  borderRadius: "12px",
                  fontWeight: 700,
                  fontSize: "1rem",
                  letterSpacing: "0.02em",
                  textTransform: "uppercase",
                  backgroundColor: "#f97316",
                  boxShadow: "0 10px 15px -3px rgba(249,115,22,0.3)",
                  transition: "all .2s",
                  "&:hover": {
                    backgroundColor: "#ea580c",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                Book Now
              </Button>

              <p className="mt-4 text-center text-xs text-slate-500">
                Free cancellation up to 24 hours before departure
              </p>

              <Divider sx={{ my: 3 }} />

              {/* Inquiry toggle */}
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setShowInquiryForm(!showInquiryForm)}
                startIcon={showInquiryForm ? <CloseIcon /> : <MailIcon />}
                sx={{
                  py: 1.5,
                  borderRadius: "12px",
                  borderWidth: 2,
                  borderColor: "#fed7aa",
                  color: "#c2410c",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  fontSize: "0.85rem",
                  "&:hover": {
                    borderWidth: 2,
                    borderColor: "#fb923c",
                    backgroundColor: "#f8fafc",
                  },
                }}
              >
                {showInquiryForm ? "Hide Inquiry" : "Inquire / Ask a Question"}
              </Button>

              {/* Desktop: inline form. Mobile uses the bottom sheet below. */}
              {showInquiryForm && (
                <div className="mt-4 hidden lg:block">{inquiryForm}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Booking Sheet Modal */}
      <TourBookingSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        tour={tour}
        travelDate={travelDate}
        travellerCount={travellerCount}
        adultsCount={totalAdults}
        kidsCount={totalChildren}
        coupon={couponData}
        discount={discountAmount}
        selectedDeparture={selectedDeparture}
        selectedHotels={selectedHotelsForBooking}
        hotelAddonTotal={hotelAddonTotal}
        selectedTransport={selectedTransport}
        transportAddon={transportAddon}
        selectedSightseeing={selectedSightseeingItems}
        sightseeingAddonTotal={sightseeingAddonTotal}
        hiddenChargesPerPerson={hiddenChargesPerPerson}
        rooms={totalRooms}
        roomConfig={roomConfig}
        kidsDiscountPct={kidsDiscountPct}
        kidsPricePerPerson={kidsPricePerPerson}
        eventId={pendingEventId}
        displayPrice={effectivePrice}
      />

      {/* Terms modal */}
      {termsModalOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/70 flex items-end sm:items-center justify-center sm:p-6">
          <div className="bg-white w-full sm:max-w-4xl max-h-[92dvh] sm:max-h-[90vh] flex flex-col shadow-2xl rounded-t-3xl sm:rounded-3xl overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-gray-200 shrink-0">
              <div className="flex justify-between items-center gap-3">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900">
                  Terms & Conditions
                </h2>
                <button
                  onClick={() => setTermsModalOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-xl"
                >
                  <CloseIcon className="w-6 h-6 text-gray-500" />
                </button>
              </div>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                Tours | Required before booking
              </p>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
              {serviceTerms ? (
                <div
                  className="prose prose-sm sm:prose-lg max-w-none break-words [&_img]:max-w-full [&_img]:h-auto [&_table]:block [&_table]:overflow-x-auto"
                  dangerouslySetInnerHTML={{ __html: serviceTerms }}
                />
              ) : (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
                  <p className="text-gray-500">Loading terms...</p>
                </div>
              )}
            </div>

            <div className="shrink-0 p-4 sm:p-6 border-t bg-gradient-to-r from-orange-50 to-yellow-50 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <label className="flex items-start gap-3 p-3 sm:p-4 bg-white rounded-xl border-2 border-dashed border-orange-300 hover:border-orange-500 cursor-pointer mb-4">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  className="mt-1 h-5 w-5 text-orange-600 border-2 border-gray-300 rounded focus:ring-orange-500 flex-shrink-0"
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                <span className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                  I have read and agree to the{" "}
                  <strong className="text-orange-700 font-bold">
                    Terms & Conditions
                  </strong>{" "}
                  above
                </span>
              </label>

              <div className="flex flex-col-reverse sm:flex-row gap-3">
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setTermsModalOpen(false)}
                  sx={{ textTransform: "none", py: 1.25 }}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={!termsAccepted}
                  onClick={() => {
                    setTermsModalOpen(false);
                    const bookingEventId = crypto.randomUUID();
                    trackEvent(
                      "InitiateCheckout",
                      {
                        content_ids: [tour._id],
                        content_type: "tour_package",
                        value: effectivePrice,
                        currency: "INR",
                        num_items: totalGuests,
                      },
                      bookingEventId,
                    );
                    setSheetOpen(true);
                    setPendingEventId(bookingEventId);
                  }}
                  sx={{
                    backgroundColor: "#f97316",
                    "&:hover": { backgroundColor: "#ea580c" },
                    "&:disabled": {
                      backgroundColor: "#fee2e2",
                      color: "#fca5a5",
                    },
                    textTransform: "none",
                    fontWeight: "bold",
                    py: 1.25,
                    boxShadow: "0 4px 12px rgba(249, 115, 22, 0.4)",
                  }}
                >
                  Accept & Continue
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Coupon modal */}
      {couponModalOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end md:items-center justify-center">
          <div className="bg-white w-full md:max-w-md rounded-t-2xl md:rounded-2xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">Available Coupons</h2>
              <button onClick={() => setCouponModalOpen(false)}>✕</button>
            </div>

            {couponLoading ? (
              <p className="text-center text-gray-500">Loading...</p>
            ) : availableCoupons.length === 0 ? (
              <p className="text-center text-gray-500">No coupons available</p>
            ) : (
              <div className="space-y-3">
                {availableCoupons.map((c) => (
                  <div
                    key={c._id}
                    className="border rounded-lg p-4 flex justify-between items-center"
                  >
                    <div>
                      <p className="font-bold text-orange-700">
                        {c.coupon_code}
                      </p>
                      <p className="text-sm text-gray-600">
                        Save ₹{c.coupon_amount || 0}
                      </p>
                      {c.min_order_value > 0 && (
                        <p className="text-xs text-gray-400">
                          Min order ₹{c.min_order_value}
                        </p>
                      )}
                    </div>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleSelectCoupon(c)}
                    >
                      Apply
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile inquiry bottom sheet */}
      {showInquiryForm && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 lg:hidden">
          <div className="relative bg-white w-full max-h-[90vh] rounded-t-2xl shadow-2xl overflow-y-auto mb-12">
            <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">
                Send an Inquiry
              </h2>
              <button
                onClick={() => setShowInquiryForm(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none"
              >
                ×
              </button>
            </div>
            <div className="px-4 pb-6 pt-3">{inquiryForm}</div>
          </div>
        </div>
      )}

      {changeHotelStop && (
        <ChangeHotelPanel
          stop={changeHotelStop}
          currentSelection={selectedHotels[changeHotelStop.key] || null}
          allowNoHotel={tour.stay_policy !== "mandatory"}
          onSelect={(hotel, room) => {
            setSelectedHotels((prev) => ({
              ...prev,
              [changeHotelStop.key]: { hotel, room },
            }));
            setRoomConfig([{ adults: 2, children: 0 }]);
          }}
          onClearHotel={() => {
            setSelectedHotels((prev) => {
              const next = { ...prev };
              delete next[changeHotelStop.key];
              return next;
            });
            setRoomConfig([{ adults: 2, children: 0 }]);
          }}
          onClose={() => setChangeHotelStop(null)}
        />
      )}

      {showTransportList && Array.isArray(tour.transport) && (
        <TransportOptionsPanel
          transport={tour.transport}
          selectedTransport={selectedTransport}
          totalGuests={totalGuests}
          onSelect={(t) => {
            setSelectedTransport(t);
            setTransportAutoSelected(false);
            setTransportDismissed(false);
          }}
          onClose={() => setShowTransportList(false)}
        />
      )}

      {/* Mobile sticky bar */}
      {showStickyBar && !isEmbedded && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-10px_20px_rgba(0,0,0,0.05)] lg:hidden">
          <div className="flex items-center justify-between gap-2 w-full min-w-0">
            <div className="flex-shrink-0 min-w-0">
              <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wide">
                From
              </p>
              <p className="text-base font-bold text-slate-900 truncate">
                ₹{effectivePrice.toLocaleString("en-IN")}
              </p>
            </div>

            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => setShowInquiryForm(true)}
                className="border-2 border-orange-500 text-orange-600 font-bold px-3 py-2 rounded-xl text-xs max-w-[110px] truncate"
              >
                Send Inquiry
              </button>
              <button
                onClick={handleBookNow}
                className="border-2 border-orange-500 bg-orange-500 hover:bg-orange-600 text-white font-bold px-3 py-2 rounded-xl shadow-lg shadow-orange-500/30 text-xs max-w-[110px] truncate"
              >
                Book Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TourDetail;
